import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const canManage = user && (user.role === 'admin' || user.role === 'manager');

  const [jobByPo, setJobByPo] = useState({}); // po_id -> production job id
  const [addingFor, setAddingFor] = useState(null); // po_id currently being scheduled
  const [addDate, setAddDate] = useState('');

  async function load() {
    const res = await apiFetch('/api/pos');
    if (res.ok) {
      const d = await res.json();
      setPos(d.purchase_orders);
      // best-effort: find which POs already have a production job
      const entries = await Promise.all(d.purchase_orders.map(async (po) => {
        try {
          const jr = await apiFetch(`/api/production/by-po/${po.id}`);
          if (jr.ok) { const jd = await jr.json(); return [po.id, jd.job ? jd.job.id : null]; }
        } catch (e) { /* ignore */ }
        return [po.id, null];
      }));
      const map = {};
      entries.forEach(([k, v]) => { if (v) map[k] = v; });
      setJobByPo(map);
    }
  }

  async function addToCalendar(poId) {
    const res = await apiFetch('/api/production', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_id: poId, ship_by_date: addDate || null }),
    });
    if (res.ok) { const d = await res.json(); setJobByPo((m) => ({ ...m, [poId]: d.job.id })); setAddingFor(null); setAddDate(''); }
  }
  useEffect(() => { load(); }, []);

  async function refresh() {
    setMsg(''); setBusy(true);
    try {
      const res = await apiFetch('/api/pos/refresh', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setMsg(`Synced ${d.synced} open PO${d.synced === 1 ? '' : 's'} from Zoho.`); load(); }
      else if (res.status === 503) setMsg('Zoho is not configured yet.');
      else if (res.status === 502) setMsg('Could not reach Zoho — cache unchanged.');
      else setMsg(d.error || 'Refresh failed.');
    } catch (e) {
      setMsg('Could not reach the server.');
    } finally { setBusy(false); }
  }

  function fmtMoney(n, cur) {
    if (n == null) return '—';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || 'USD' }).format(n); }
    catch { return String(n); }
  }
  function fmtDate(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return '—'; } }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Open Purchase Orders</h1>
        <p>Open POs from Zoho Books. A PO that closes in Zoho drops off on the next refresh.</p>
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head">
          <h2>Open POs</h2>
          {canManage && (
            <button className="hq-btn" onClick={refresh} disabled={busy}>
              {busy ? 'Syncing…' : 'Refresh from Zoho'}
            </button>
          )}
        </div>
        {msg && <p className="hq-msg" style={{ margin: '14px 20px 0' }}>{msg}</p>}
        <table className="hq-table">
          <thead>
            <tr><th>PO #</th><th>Vendor</th><th>Order date</th><th>Delivery</th><th>Total</th><th>Production</th></tr>
          </thead>
          <tbody>
            {pos.map((p) => (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/purchase-orders/${p.id}`)}>
                <td>{p.po_number || '—'}</td>
                <td>{p.vendor_name || '—'}</td>
                <td>{fmtDate(p.order_date)}</td>
                <td>{fmtDate(p.delivery_date)}</td>
                <td>{fmtMoney(p.total, p.currency_code)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {jobByPo[p.id] ? (
                    <button className="hq-btn hq-btn-ghost" onClick={() => navigate(`/production/${jobByPo[p.id]}`)}>View in Calendar</button>
                  ) : !canManage ? (
                    <span className="hq-role">—</span>
                  ) : addingFor === p.id ? (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input className="hq-input" type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} />
                      <button className="hq-btn" onClick={() => addToCalendar(p.id)}>Add</button>
                      <button className="hq-btn hq-btn-ghost" onClick={() => { setAddingFor(null); setAddDate(''); }}>Cancel</button>
                    </span>
                  ) : (
                    <button className="hq-btn hq-btn-ghost" onClick={() => { setAddingFor(p.id); setAddDate(p.delivery_date ? String(p.delivery_date).slice(0,10) : ''); }}>Add to Production Calendar</button>
                  )}
                </td>
              </tr>
            ))}
            {pos.length === 0 && (
              <tr><td className="hq-empty" colSpan={6}>No open POs. Use “Refresh from Zoho”.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
